import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { ArchiveDialog } from "@/components/ArchiveDialog";
import { RowActions } from "@/components/RowActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Copy, Pencil, Plane, Calendar, ScrollText, Lock, Unlock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", code: "", departure_date: "", return_date: "", order_closing_date: "",
  australia_location: "", indonesia_destination: "", exchange_rate: "11000", rate_mode: "manual",
  default_margin_percent: "15", default_rounding_method: "5000", default_custom_step: 0,
  default_jasa_titip_type: "fixed", default_jasa_titip_value: "50000", default_delivery_fee: "30000",
  baggage_cost: "0", other_expenses: "0", notes: "", status: "Planning",
};

export default function Trips() {
  const { constants, loadTrips } = useApp();
  const { isAdmin } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dupOpen, setDupOpen] = useState(false);
  const [dup, setDup] = useState({ name: "", code: "", source_trip_id: "" });
  const [liveRate, setLiveRate] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [reconTarget, setReconTarget] = useState(null);
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/trips", { params: { include_deleted: isAdmin && showDeleted } }); setTrips(r.data); } finally { setLoading(false); }
  }, [isAdmin, showDeleted]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };

  const fetchLive = async () => {
    try { const r = await api.get("/exchange-rate/live"); setLiveRate(r.data); set("exchange_rate", String(Math.round(Number(r.data.rate)))); set("rate_mode", "live"); toast.success(`Kurs live: ${r.data.rate}`); }
    catch { toast.error("Gagal ambil kurs live"); }
  };

  const save = async () => {
    if (!form.name) return toast.error("Nama trip wajib diisi");
    try {
      if (editing) { await api.put(`/trips/${editing.id}`, form); toast.success("Trip diperbarui"); }
      else { await api.post("/trips", form); toast.success("Trip dibuat"); }
      setOpen(false); load(); loadTrips();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const doDuplicate = async () => {
    if (!dup.name || !dup.source_trip_id) return toast.error("Lengkapi data duplikasi");
    try { await api.post("/trips/duplicate", dup); toast.success("Trip diduplikasi"); setDupOpen(false); load(); loadTrips(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const doArchive = async () => {
    await api.delete(`/trips/${archiveTarget.id}`);
    toast.success("Trip diarsipkan"); load(); loadTrips();
  };

  const openReconcile = async (t) => {
    setReconTarget(t); setReconData(null); setReconLoading(true);
    try { const r = await api.get(`/trips/${t.id}/reconcile`); setReconData(r.data); }
    catch { toast.error("Gagal memuat ringkasan"); }
    finally { setReconLoading(false); }
  };

  const toggleClose = async (close) => {
    if (!reconTarget) return;
    setReconBusy(true);
    try {
      await api.post(`/trips/${reconTarget.id}/${close ? "close" : "reopen"}`);
      toast.success(close ? "Trip ditandai selesai (ditutup)" : "Trip dibuka kembali");
      const r = await api.get(`/trips/${reconTarget.id}/reconcile`); setReconData(r.data);
      load(); loadTrips();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memperbarui status"); }
    finally { setReconBusy(false); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader title="Trip" subtitle="Kelola perjalanan belanja Anda">
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-1 cursor-pointer" data-testid="trip-show-deleted">
            <Checkbox checked={showDeleted} onCheckedChange={setShowDeleted} />Tampilkan Terhapus
          </label>
        )}
        {isAdmin && <Button variant="outline" onClick={() => { setDup({ name: "", code: "", source_trip_id: trips[0]?.id || "" }); setDupOpen(true); }} data-testid="trip-duplicate-button"><Copy className="h-4 w-4 mr-1.5" />Duplikat</Button>}
        {isAdmin && <Button onClick={openNew} data-testid="trip-create-button"><Plus className="h-4 w-4 mr-1.5" />Trip Baru</Button>}
      </PageHeader>

      {trips.length === 0 ? <EmptyState title="Belum ada trip" subtitle="Buat trip pertama Anda untuk mulai mengelola order." action={isAdmin && <Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Trip Baru</Button>} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trips.map((t) => (
            <div key={t.id} className="rounded-xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="trip-card">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Plane className="h-5 w-5 text-primary" /></div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={t.status} />
                  {t.closed && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1"><Lock className="h-3 w-3" />Ditutup</span>}
                  {t.deleted && <span className="text-xs text-destructive">Terarsip</span>}
                </div>
              </div>
              <p className="font-heading font-semibold mt-3">{t.name}</p>
              <p className="text-xs text-muted-foreground">{t.code} · {t.australia_location} → {t.indonesia_destination}</p>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Kurs</span><span className="font-mono">{t.exchange_rate} <span className="text-xs text-muted-foreground">({t.rate_mode})</span></span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Margin</span><span className="font-mono">{t.default_margin_percent}%</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Jasa titip</span><span className="font-mono">{formatIDR(t.default_jasa_titip_value)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground"><Calendar className="h-3 w-3 inline mr-1" />Tutup order</span><span>{formatDate(t.order_closing_date)}</span></div>
              </div>
              <div className="mt-4 flex gap-2 items-center">
                {!t.deleted && <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(t)} data-testid="trip-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>}
                {isAdmin && !t.deleted && <RowActions onEdit={() => openEdit(t)} onArchive={() => setArchiveTarget(t)} testId="trip" />}
              </div>
              {!t.deleted && (
                <Button size="sm" variant="secondary" className="w-full mt-2" onClick={() => openReconcile(t)} data-testid="trip-reconcile-button">
                  <ScrollText className="h-3.5 w-3.5 mr-1.5" />Ringkasan / Tutup Trip
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Trip" : "Trip Baru"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nama Trip" required><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="trip-name-input" /></Field>
            <Field label="Kode Trip"><Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="otomatis jika kosong" /></Field>
            <Field label="Lokasi Australia"><Input value={form.australia_location} onChange={(e) => set("australia_location", e.target.value)} /></Field>
            <Field label="Tujuan Indonesia"><Input value={form.indonesia_destination} onChange={(e) => set("indonesia_destination", e.target.value)} /></Field>
            <Field label="Tgl Berangkat"><Input type="date" value={form.departure_date || ""} onChange={(e) => set("departure_date", e.target.value)} /></Field>
            <Field label="Tgl Kembali"><Input type="date" value={form.return_date || ""} onChange={(e) => set("return_date", e.target.value)} /></Field>
            <Field label="Tgl Tutup Order"><Input type="date" value={form.order_closing_date || ""} onChange={(e) => set("order_closing_date", e.target.value)} /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="trip-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(constants?.trip_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Kurs AUD→IDR" hint={liveRate ? `Live: ${liveRate.rate}` : ""}>
              <div className="flex gap-2">
                <Input value={form.exchange_rate} onChange={(e) => { set("exchange_rate", e.target.value); set("rate_mode", "manual"); }} data-testid="trip-rate-input" />
                <Button type="button" variant="outline" size="sm" onClick={fetchLive} data-testid="trip-live-rate">Live</Button>
              </div>
            </Field>
            <Field label="Margin default (%)"><Input type="number" value={form.default_margin_percent} onChange={(e) => set("default_margin_percent", e.target.value)} /></Field>
            <Field label="Tipe Jasa Titip">
              <Select value={form.default_jasa_titip_type} onValueChange={(v) => set("default_jasa_titip_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fixed">Nominal Tetap</SelectItem><SelectItem value="percentage">Persentase</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Jasa Titip default"><Input type="number" value={form.default_jasa_titip_value} onChange={(e) => set("default_jasa_titip_value", e.target.value)} /></Field>
            <Field label="Ongkir default (Rp)"><Input type="number" value={form.default_delivery_fee} onChange={(e) => set("default_delivery_fee", e.target.value)} /></Field>
            <Field label="Pembulatan default">
              <Select value={form.default_rounding_method} onValueChange={(v) => set("default_rounding_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(constants?.rounding_methods || []).map((s) => <SelectItem key={s} value={s}>{s === "none" ? "Tanpa" : s === "custom" ? "Kustom" : `Rp${Number(s).toLocaleString("id-ID")}`}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Biaya Bagasi (Rp)"><Input type="number" value={form.baggage_cost} onChange={(e) => set("baggage_cost", e.target.value)} /></Field>
            <div className="sm:col-span-2"><Field label="Catatan"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="trip-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Duplikat Trip</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Trip sumber">
              <Select value={dup.source_trip_id} onValueChange={(v) => setDup((d) => ({ ...d, source_trip_id: v }))}>
                <SelectTrigger data-testid="dup-source-select"><SelectValue placeholder="Pilih trip" /></SelectTrigger>
                <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Nama trip baru"><Input value={dup.name} onChange={(e) => setDup((d) => ({ ...d, name: e.target.value }))} data-testid="dup-name-input" /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDupOpen(false)}>Batal</Button><Button onClick={doDuplicate} data-testid="dup-save-button">Duplikat</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog open={!!archiveTarget} onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}
        title="Arsipkan Trip?"
        description={`Trip "${archiveTarget?.name || ""}" akan diarsipkan dan disembunyikan dari daftar. Dapat dipulihkan dari Kotak Sampah.`}
        onConfirm={doArchive} testId="trip-archive" />

      {/* Reconciliation dialog */}
      <Dialog open={!!reconTarget} onOpenChange={(v) => { if (!v) { setReconTarget(null); setReconData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="trip-reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-primary" />Ringkasan Trip {reconTarget ? `· ${reconTarget.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {reconLoading || !reconData ? (
            <div className="py-10 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 mr-2 animate-spin" />Memuat ringkasan…</div>
          ) : (() => {
            const rep = reconData.report;
            const usedCost = rep.cost.actual_purchase_idr > 0 ? rep.cost.actual_purchase_idr : rep.cost.est_purchase_idr;
            const feeIncome = (rep.profit.service_fee_profit || 0) + (rep.profit.delivery_income || 0);
            const cards = [
              ["Pendapatan (bersih)", rep.sales.net_sales, "text-foreground"],
              ["Total dibayar", rep.profit.total_paid, "text-foreground"],
              ["Piutang (belum lunas)", rep.profit.outstanding_balance, "text-[hsl(var(--warning))]"],
              ["Biaya belanja (real)", usedCost, "text-foreground"],
              ["Pengeluaran lain", rep.cost.total_expenses, "text-foreground"],
              ["Pendapatan fee (jastip+ongkir)", feeIncome, "text-foreground"],
              ["Laba kotor", rep.profit.actual_gross_profit, "text-primary"],
              ["Laba bersih", rep.profit.actual_net_profit, "text-primary"],
            ];
            return (
              <>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <StatusBadge status={reconData.trip.status} />
                  <span className="text-muted-foreground">{rep.sales.total_orders} order · {rep.sales.products_sold} produk terjual</span>
                  {reconData.trip.closed && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1"><Lock className="h-3 w-3" />Ditutup {reconData.trip.closed_at ? `· ${formatDate(reconData.trip.closed_at)}` : ""}</span>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  {cards.map(([label, val, cls]) => (
                    <div key={label} className="rounded-xl border border-border bg-card p-3" data-testid="trip-reconcile-metric">
                      <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
                      <p className={`font-heading text-sm font-semibold mt-1 ${cls}`}>{formatIDR(val)}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border border-border bg-secondary/40 p-3 mt-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Margin laba</span><span className="font-semibold">{rep.profit.profit_margin_pct}%</span></div>
                  <div className="flex justify-between mt-1"><span className="text-muted-foreground">Rata-rata laba / order</span><span className="font-mono">{formatIDR(rep.profit.avg_profit_per_order)}</span></div>
                  {rep.cancelled_count > 0 && <div className="flex justify-between mt-1"><span className="text-muted-foreground">Order dibatalkan</span><span>{rep.cancelled_count}</span></div>}
                </div>
                {isAdmin && (
                  <DialogFooter className="mt-4">
                    {reconData.trip.closed ? (
                      <Button variant="outline" onClick={() => toggleClose(false)} disabled={reconBusy} data-testid="trip-reopen-button">
                        {reconBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Unlock className="h-4 w-4 mr-1.5" />}Buka kembali trip
                      </Button>
                    ) : (
                      <Button onClick={() => toggleClose(true)} disabled={reconBusy} data-testid="trip-close-button">
                        {reconBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Lock className="h-4 w-4 mr-1.5" />}Tandai trip selesai
                      </Button>
                    )}
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
